import {
  CSSProperties,
  ReactNode,
  useEffect,
  useState,
  forwardRef,
} from 'react';
import {
  SnackbarProvider,
  CustomContentProps,
  SnackbarContent,
  closeSnackbar,
} from 'notistack';
import { useSelector } from 'react-redux';
import { X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { selectIsTrialEligible } from '../../../../slices/subscriptions/subscriptionsSlice';
import { getUpgradeCtaLabel } from '../../../../utils/upgradeCopy';
import { capture } from '../../../../utils/analytics';
import { Capacitor } from '@capacitor/core';

// Registers the custom `paywall` variant used by free-tier paywall toasts and
// the `reload` variant used by persistent load errors so
// `enqueueSnackbar(msg, { variant: 'paywall' | 'reload' })` type-checks.
declare module 'notistack' {
  interface VariantOverrides {
    paywall: true;
    reload: true;
  }
}

// The native shell makes every toast click-through (see `_notistack.scss`);
// a control (or a wrapper around a `Button`) opts back in with this class,
// together with everything inside it, so it stays tappable.
const INTERACTIVE_CLASS = 'snackbar-interactive';

interface ResponsiveSnackbarProviderProps {
  children: ReactNode;
}

const TRANSITION_TIME = 500;

// The dark glass surface every toast sits on. The progress bar's place and
// track live in `_notistack.scss`.
const SNACKBAR_SURFACE_STYLE: CSSProperties = {
  WebkitBackdropFilter: 'blur(20px)',
  backdropFilter: 'blur(20px)',
  backgroundColor: 'rgba(0,0,0,.8)',
  color: 'var(--TYPE-BODY-DARK)',
  borderRadius: '.75rem',
  padding: '0',
  minWidth: 'fit-content',
  width: 'fit-content',
  maxWidth: '100%',
  margin: '0 auto',
  transition: `opacity ${TRANSITION_TIME}ms ease-out`,
};

const CustomSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ message, autoHideDuration, className }, ref) => {
    // Accept className
    const [progress, setProgress] = useState(100);

    useEffect(() => {
      if (!autoHideDuration) return;

      const adjustedDuration = autoHideDuration - TRANSITION_TIME;
      const interval = 100;
      const totalSteps = Math.floor(adjustedDuration / interval);
      const decrement = 100 / totalSteps;
      let steps = 0;

      const timer = setInterval(() => {
        setProgress((prev) => {
          steps += 1;
          const nextProgress = prev - decrement;

          if (steps >= totalSteps) {
            clearInterval(timer);
            return 0;
          }

          return Math.max(nextProgress, 0);
        });
      }, interval);

      return () => clearInterval(timer);
    }, [autoHideDuration]);

    return (
      <SnackbarContent
        ref={ref}
        className={className}
        style={SNACKBAR_SURFACE_STYLE}
      >
        <div className="notistack-content">
          {message}
          <Progress className="h-1" color="inverse" value={progress} />
        </div>
      </SnackbarContent>
    );
  }
);

CustomSnackbar.displayName = 'CustomSnackbar';

// Free-tier paywall toast: no auto-dismiss (X on the left to close) plus a
// brand-gradient upgrade CTA on the right. Enqueue via `enqueuePaywallToast`.
const PaywallSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message, className }, ref) => {
    const isTrialEligible = useSelector(selectIsTrialEligible);

    const handleUpgrade = () => {
      capture('paywall_toast_cta_clicked', { limitType: 'upload' });
      window.open('/pricing', '_blank');
      closeSnackbar(id);
    };

    return (
      <SnackbarContent
        ref={ref}
        className={className}
        style={SNACKBAR_SURFACE_STYLE}
      >
        <div className="flex items-center gap-3 px-3 py-2">
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => closeSnackbar(id)}
            className={cn(
              INTERACTIVE_CLASS,
              'flex shrink-0 items-center justify-center rounded-full p-1 text-neutral-25 opacity-70 transition-opacity hover:opacity-100'
            )}
          >
            <X size={16} weight="bold" />
          </button>
          <span className="text-sm text-neutral-25">{message}</span>
          {Capacitor.getPlatform() !== 'android' && (
            <span className={cn(INTERACTIVE_CLASS, 'shrink-0')}>
              <Button
                variant="brand-gradient"
                size="sm"
                onClick={handleUpgrade}
              >
                {getUpgradeCtaLabel(isTrialEligible)}
              </Button>
            </span>
          )}
        </div>
      </SnackbarContent>
    );
  }
);

PaywallSnackbar.displayName = 'PaywallSnackbar';

// Persistent load-error toast: no auto-dismiss, an X to close it, and a Reload
// button that restarts the app (web page or native web view).
const ReloadSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message, className }, ref) => (
    <SnackbarContent
      ref={ref}
      className={className}
      style={SNACKBAR_SURFACE_STYLE}
    >
      <div className="flex items-center gap-3 px-3 py-2">
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => closeSnackbar(id)}
          className={cn(
            INTERACTIVE_CLASS,
            'flex shrink-0 items-center justify-center rounded-full p-1 text-neutral-25 opacity-70 transition-opacity hover:opacity-100'
          )}
        >
          <X size={16} weight="bold" />
        </button>
        <span className="text-sm text-neutral-25">{message}</span>
        <span className={cn(INTERACTIVE_CLASS, 'shrink-0')}>
          <Button
            variant="glass"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        </span>
      </div>
    </SnackbarContent>
  )
);

ReloadSnackbar.displayName = 'ReloadSnackbar';

export function ResponsiveSnackbarProvider({
  children,
}: ResponsiveSnackbarProviderProps) {
  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      Components={{
        default: CustomSnackbar,
        paywall: PaywallSnackbar,
        reload: ReloadSnackbar,
      }}
      domRoot={document.body}
    >
      {children}
    </SnackbarProvider>
  );
}
